/**
 * Company: SPHMedia
 * Description: [Fetches Google Ratings for list of restaurants]
 */

import { CLIENT_ID_HEADER } from "@api/constant";
import fetchBHApi from "@helper/fetchBHApi";
import { serializeRatingsQuery } from "@helper/serializeRatingsQuery";
import { GoogleRatingResponse, RatingInput } from "@typings/GooglePlaces";
import { useCallback, useEffect, useRef, useState } from "react";

import { createBatches } from "../helper/utils";

type UseGoogleRatingsData = {
  data: Record<string, GoogleRatingResponse>;
  isLoading: boolean;
  error: Error | null;
};

type UseGoogleRatingsProps = {
  items: RatingInput[];
  initialData: Record<string, GoogleRatingResponse>;
};

export default function useGoogleRatings({
  items,
  initialData,
}: UseGoogleRatingsProps): UseGoogleRatingsData {
  const [data, setData] =
    useState<Record<string, GoogleRatingResponse>>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Keep track of fetched articleIds
  const fetchedIds = useRef<Set<string>>(new Set(Object.keys(initialData)));

  // Keep track of inflight articleIds
  const inflightIds = useRef<Set<string>>(new Set());

  // track component mounted state
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Fetch ratings for multiple items
   */
  const fetchBatchRatings = useCallback(async (toFetch: RatingInput[]) => {
    if (!toFetch.length) return;

    if (isMounted.current) setIsLoading(true);

    try {
      const { names, ids } = serializeRatingsQuery(toFetch);

      const params = new URLSearchParams();
      params.append("names", names);
      params.append("articleIds", ids);

      const res = await fetchBHApi<Record<string, GoogleRatingResponse>>(
        `getGoogleRating?${params.toString()}`,
        "GET",
        undefined,
        { "x-client-id": CLIENT_ID_HEADER },
      );

      // Save response per articleId
      if (isMounted.current) {
        setData((prev) => ({
          ...prev,
          ...res, // { [articleId]: ratingData }
        }));
      }

      // Mark fetched
      toFetch.forEach((item) => fetchedIds.current.add(item.articleId));
    } catch (err) {
      if (isMounted.current) {
        setError(err as Error);
      }
    } finally {
      // Mark inflight as done
      toFetch.forEach((item) => inflightIds.current.delete(item.articleId));
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  /**
   * Watch items and detect unseen ones
   */
  useEffect(() => {
    if (!items.length) return;

    const newItems = items.filter((item) => {
      const id = item.articleId;
      return !fetchedIds.current.has(id) && !inflightIds.current.has(id);
    });

    if (newItems.length === 0) return;

    // Mark inflight
    newItems.forEach((item) => inflightIds.current.add(item.articleId));

    // Perform batch fetch (max 8)
    const BATCH_SIZE = 8;
    const batches = createBatches(newItems, BATCH_SIZE);

    // Fire each batch
    batches.forEach((batch) => {
      void fetchBatchRatings(batch);
    });
  }, [items, fetchBatchRatings]);

  return {
    data,
    isLoading,
    error,
  };
}
